import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { X } from "lucide-react";
import { toast } from "react-toastify";
import { api } from "../../../lib/axios";
import { getPlansAbonnement, type PlanAbonnement } from "../../../lib/api/plansAbonnement";
import AdminClientRequestStatusBadge from "./AdminClientRequestStatusBadge";

type DrawerProps = {
  open: boolean;
  publicId: string | null;
  onClose: () => void;
  onChanged: () => void;
};

export default function AdminClientRequestDetailDrawer(props: DrawerProps): React.JSX.Element | null {
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState<Record<string, unknown> | null>(null);
  const [acceptComment, setAcceptComment] = useState("");
  const [newPlannedAt, setNewPlannedAt] = useState("");
  const [contratId, setContratId] = useState("");
  const [rejectComment, setRejectComment] = useState("");
  const [resolveComment, setResolveComment] = useState("");
  const [plannedAt, setPlannedAt] = useState("");
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!props.publicId) return;
    setLoading(true);
    try {
      const { data } = await api.get<{ success?: boolean; item?: Record<string, unknown> }>(
        `/supervisor/client-requests/${encodeURIComponent(props.publicId)}`
      );
      setDetail(data.item ?? null);
    } catch {
      toast.error("Impossible de charger le détail.");
      setDetail(null);
    } finally {
      setLoading(false);
    }
  }, [props.publicId]);

  useEffect(() => {
    if (props.open && props.publicId) void load();
  }, [props.open, props.publicId, load]);

  const [urgTarifPlans, setUrgTarifPlans] = useState<PlanAbonnement[]>([]);
  const [urgTarifPlansLoading, setUrgTarifPlansLoading] = useState(false);
  const [urgReferentPlanId, setUrgReferentPlanId] = useState("");
  const [urgMontant, setUrgMontant] = useState("");
  const [urgDevise, setUrgDevise] = useState("");

  useEffect(() => {
    setUrgReferentPlanId("");
    setUrgMontant("");
    setUrgDevise("");
    setUrgTarifPlans([]);
  }, [props.publicId]);

  const pid = props.publicId ?? "";

  const openUrgenceTarifForm =
    props.open &&
    pid.startsWith("d-") &&
    detail != null &&
    String((detail as { source?: string }).source ?? "") !== "reclamation" &&
    String((detail as { statut?: string }).statut ?? "") === "EN_ATTENTE" &&
    String((detail as { typeDemande?: string }).typeDemande ?? "") === "INTERVENTION_URGENTE";

  useEffect(() => {
    if (!openUrgenceTarifForm) return;
    let cancelled = false;
    setUrgTarifPlansLoading(true);
    void (async () => {
      try {
        const rows = await getPlansAbonnement();
        if (!cancelled) {
          setUrgTarifPlans(rows.filter((p) => p.actif !== false));
        }
      } catch {
        if (!cancelled) setUrgTarifPlans([]);
      } finally {
        if (!cancelled) setUrgTarifPlansLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [openUrgenceTarifForm]);

  const buildTarifIntervention = useCallback(():
    | { montantForfait?: number; devise?: string; referentPlanId?: number }
    | undefined
    | null => {
    const planNum = urgReferentPlanId.trim() ? Number(urgReferentPlanId) : NaN;
    const amtRaw = urgMontant.trim().replace(",", ".");
    const amt = amtRaw === "" ? NaN : Number(amtRaw);
    const hasPlan = Number.isInteger(planNum) && planNum > 0;
    const hasAmt = Number.isFinite(amt) && amt > 0;
    if (hasPlan && hasAmt) {
      toast.warning("Choisissez soit un forfait catalogue, soit un montant manuel — pas les deux.");
      return null;
    }
    if (!hasPlan && !hasAmt) return undefined;
    if (hasAmt) {
      const d = urgDevise.trim().toUpperCase().slice(0, 12);
      return {
        montantForfait: amt,
        ...(d ? { devise: d } : {}),
      };
    }
    return { referentPlanId: planNum };
  }, [urgReferentPlanId, urgMontant, urgDevise]);

  const postAction = async (path: string, body: Record<string, unknown>) => {
    setBusy(path);
    try {
      await api.post(`/supervisor/client-requests/${encodeURIComponent(pid)}${path}`, body);
      toast.success("Enregistré.");
      props.onChanged();
      await load();
    } catch (e: unknown) {
      const ax = e as { response?: { data?: { message?: string; error?: string } } };
      toast.error(ax.response?.data?.message ?? ax.response?.data?.error ?? "Action impossible.");
    } finally {
      setBusy(null);
    }
  };

  if (!props.open) return null;

  const raw = detail;
  const isRec = raw?.source === "reclamation";
  const row = isRec ? null : raw;
  const recDto = isRec ? (raw?.dto as Record<string, unknown> | undefined) : undefined;

  const statut = String(row?.statut ?? "");
  const typeDemande = String(row?.typeDemande ?? "");
  const motif = String(row?.motif ?? recDto?.description ?? "");
  const statusCanon = String(row?.status ?? recDto?.status ?? "");

  const timeline = Array.isArray(row?.timeline)
    ? (row?.timeline as { action?: string; createdAt?: string; label?: string }[])
    : [];

  const isDemande = pid.startsWith("d-");
  const showPassageAccept = isDemande && statut === "EN_ATTENTE" && typeDemande === "MODIFICATION_PASSAGE";
  const showUrgenceConvert = isDemande && statut === "EN_ATTENTE" && typeDemande === "INTERVENTION_URGENTE";
  const showResolveDemande = isDemande && statut === "ACCEPTEE";

  const input =
    "w-full rounded-lg border border-white/10 bg-slate-950 px-3 py-2 text-sm text-slate-100 outline-none placeholder:text-slate-500 focus:border-emerald-500/40 focus:ring-2 focus:ring-emerald-500/20";

  return (
    <div
      className="fixed inset-0 z-50 flex justify-end bg-black/60 backdrop-blur-[1px]"
      role="presentation"
      onClick={() => props.onClose()}
    >
      <div
        className="flex h-full w-full max-w-lg flex-col border-l border-white/10 bg-slate-900 text-slate-100 shadow-2xl shadow-black/50"
        role="dialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-white/10 px-5 py-4">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-emerald-400/90">Demande client</p>
            <p className="font-mono text-sm font-bold text-white">{pid}</p>
          </div>
          <button
            type="button"
            onClick={() => props.onClose()}
            className="rounded-lg border border-white/10 bg-slate-800/80 p-2 text-slate-300 transition hover:bg-slate-800"
            aria-label="Fermer"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          {loading ? (
            <p className="text-sm text-slate-400">Chargement…</p>
          ) : (
            <>
              <div className="flex flex-wrap gap-2">
                <AdminClientRequestStatusBadge status={statusCanon || statut} />
              </div>

              <section className="mt-4 space-y-2 text-sm">
                <h3 className="text-[11px] font-bold uppercase tracking-wide text-slate-500">Description</h3>
                <p className="whitespace-pre-wrap text-slate-200">{motif || "—"}</p>
              </section>

              {timeline.length > 0 ? (
                <section className="mt-6">
                  <h3 className="text-[11px] font-bold uppercase tracking-wide text-slate-500">Historique</h3>
                  <ul className="mt-2 space-y-2 border-l border-white/15 pl-3">
                    {timeline.map((ev) => (
                      <li key={`${ev.action}-${ev.createdAt}`} className="text-xs text-slate-400">
                        <span className="font-semibold text-slate-200">{ev.label ?? ev.action}</span>
                        {ev.createdAt ? (
                          <span className="ml-2 text-slate-500">
                            {new Date(ev.createdAt).toLocaleString("fr-FR")}
                          </span>
                        ) : null}
                      </li>
                    ))}
                  </ul>
                </section>
              ) : null}

              {showPassageAccept ? (
                <section className="mt-8 space-y-4 rounded-xl border border-white/10 bg-slate-950/60 p-4">
                  <h3 className="text-xs font-bold uppercase tracking-wide text-slate-400">Accepter (replanifier)</h3>
                  <textarea
                    value={acceptComment}
                    onChange={(e) => setAcceptComment(e.target.value)}
                    placeholder="Commentaire client (public)"
                    rows={3}
                    className={input}
                  />
                  <label className="block text-xs font-medium text-slate-400">
                    Nouvelle date / heure
                    <input
                      type="datetime-local"
                      value={newPlannedAt}
                      onChange={(e) => setNewPlannedAt(e.target.value)}
                      className={`mt-1 ${input}`}
                    />
                  </label>
                  <button
                    type="button"
                    disabled={busy !== null}
                    onClick={() =>
                      void postAction("/accept", {
                        supervisorComment: acceptComment || undefined,
                        newPlannedAt: newPlannedAt ? new Date(newPlannedAt).toISOString() : undefined,
                      })
                    }
                    className="w-full rounded-lg bg-emerald-600 py-2.5 text-sm font-semibold text-white transition hover:bg-emerald-500 disabled:opacity-50"
                  >
                    {busy === "/accept" ? "…" : "Accepter"}
                  </button>

                  <h3 className="pt-2 text-xs font-bold uppercase tracking-wide text-slate-400">Rejeter</h3>
                  <textarea
                    value={rejectComment}
                    onChange={(e) => setRejectComment(e.target.value)}
                    placeholder="Motif obligatoire"
                    rows={2}
                    className={input}
                  />
                  <button
                    type="button"
                    disabled={busy !== null || !rejectComment.trim()}
                    onClick={() => void postAction("/reject", { supervisorComment: rejectComment.trim() })}
                    className="w-full rounded-lg border border-rose-500/40 bg-rose-950/40 py-2.5 text-sm font-semibold text-rose-100 transition hover:bg-rose-950/60 disabled:opacity-50"
                  >
                    {busy === "/reject" ? "…" : "Rejeter"}
                  </button>
                </section>
              ) : null}

              {showUrgenceConvert ? (
                <section className="mt-8 space-y-3 rounded-xl border border-amber-500/25 bg-amber-950/30 p-4">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <h3 className="text-xs font-bold uppercase tracking-wide text-amber-200/90">
                      Convertir en passage
                    </h3>
                    <Link
                      to="/admin/categories-tarifs"
                      title="Plans et tarifs catalogue — pour associer nom + prix à un passage hors abonnement"
                      className="rounded-lg border border-amber-500/35 bg-amber-900/30 px-2.5 py-1 text-[11px] font-semibold text-amber-100 transition hover:border-amber-400/50 hover:bg-amber-900/50"
                    >
                      Tarif intervention urgente
                    </Link>
                  </div>
                  <label className="block text-xs font-medium text-slate-400">
                    Date / heure prévues
                    <input
                      type="datetime-local"
                      value={plannedAt}
                      onChange={(e) => setPlannedAt(e.target.value)}
                      className={`mt-1 ${input}`}
                    />
                  </label>
                  <label className="block text-xs font-medium text-slate-400">
                    Contrat actif (obligatoire)
                    <input
                      type="number"
                      value={contratId}
                      onChange={(e) => setContratId(e.target.value)}
                      className={`mt-1 ${input}`}
                    />
                  </label>

                  <div className="rounded-lg border border-amber-500/25 bg-slate-950/40 px-3 py-2">
                    <p className="text-[11px] font-semibold text-amber-100/95">Tarif hors abonnement</p>
                    <p className="mt-1 text-[11px] text-slate-500">
                      Saisissez un forfait catalogue ou un montant pour alimenter la facturation et le portail client.
                      Sinon le passage restera sans montant jusqu’à saisie manuelle en finance.
                    </p>
                    <label className="mt-3 block text-xs font-medium text-slate-400">
                      Référencer un plan catalogue
                      <select
                        value={urgReferentPlanId}
                        onChange={(e) => setUrgReferentPlanId(e.target.value)}
                        className={`mt-1 ${input}`}
                      >
                        <option value="">— Non —</option>
                        {urgTarifPlans.map((p) => (
                          <option key={p.id} value={String(p.id)}>
                            {p.nom} — {Number(p.montant).toLocaleString("fr-FR")} {p.devise}
                          </option>
                        ))}
                      </select>
                      {urgTarifPlansLoading ? (
                        <span className="mt-1 block text-[11px] text-slate-500">Chargement des offres…</span>
                      ) : null}
                    </label>
                    <label className="mt-2 block text-xs font-medium text-slate-400">
                      Ou montant forfait
                      <input
                        type="text"
                        inputMode="decimal"
                        value={urgMontant}
                        onChange={(e) => setUrgMontant(e.target.value)}
                        placeholder="Ex. 85 ou 125,50"
                        className={`mt-1 ${input}`}
                      />
                    </label>
                    <label className="mt-2 block text-xs font-medium text-slate-400">
                      Devise (montant manuel uniquement — optionnel, défaut celle du contrat)
                      <input
                        type="text"
                        value={urgDevise}
                        onChange={(e) => setUrgDevise(e.target.value)}
                        placeholder="USD ou CDF"
                        className={`mt-1 ${input}`}
                      />
                    </label>
                  </div>

                  <textarea
                    value={acceptComment}
                    onChange={(e) => setAcceptComment(e.target.value)}
                    placeholder="Commentaire interne / client (optionnel)"
                    rows={2}
                    className={input}
                  />
                  <button
                    type="button"
                    disabled={busy !== null || !plannedAt || !contratId}
                    onClick={() => {
                      const ti = buildTarifIntervention();
                      if (ti === null) return;
                      void postAction("/convert-to-passage", {
                        plannedAt: new Date(plannedAt).toISOString(),
                        contratId: Number(contratId),
                        supervisorComment: acceptComment || undefined,
                        ...(ti != null ? { tarifIntervention: ti } : {}),
                      });
                    }}
                    className="w-full rounded-lg bg-amber-600 py-2.5 text-sm font-semibold text-white transition hover:bg-amber-500 disabled:opacity-50"
                  >
                    {busy === "/convert-to-passage" ? "…" : "Convertir en passage"}
                  </button>
                </section>
              ) : null}

              {showResolveDemande ? (
                <section className="mt-6 space-y-2">
                  <h3 className="text-xs font-bold uppercase tracking-wide text-slate-400">Clôture (commentaire)</h3>
                  <textarea
                    value={resolveComment}
                    onChange={(e) => setResolveComment(e.target.value)}
                    rows={3}
                    className={input}
                  />
                  <button
                    type="button"
                    disabled={busy !== null || !resolveComment.trim()}
                    onClick={() => void postAction("/resolve", { supervisorComment: resolveComment.trim() })}
                    className="w-full rounded-lg border border-white/15 bg-slate-800 py-2.5 text-sm font-semibold text-white transition hover:bg-slate-700 disabled:opacity-50"
                  >
                    {busy === "/resolve" ? "…" : "Enregistrer clôture"}
                  </button>
                </section>
              ) : null}

              {isRec && recDto ? (
                <section className="mt-6 space-y-3 rounded-xl border border-white/10 bg-slate-950/60 p-4">
                  <h3 className="text-xs font-bold uppercase tracking-wide text-slate-400">Réclamation</h3>
                  <textarea
                    value={resolveComment}
                    onChange={(e) => setResolveComment(e.target.value)}
                    placeholder="Réponse visible côté client"
                    rows={4}
                    className={input}
                  />
                  <button
                    type="button"
                    disabled={busy !== null || !resolveComment.trim()}
                    onClick={() => void postAction("/resolve", { supervisorComment: resolveComment.trim() })}
                    className="w-full rounded-lg bg-emerald-600 py-2.5 text-sm font-semibold text-white transition hover:bg-emerald-500 disabled:opacity-50"
                  >
                    {busy === "/resolve" ? "…" : "Marquer résolu"}
                  </button>
                </section>
              ) : null}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
